/* Template-aware route shell for /s/:slug.

   Reads `site_settings.template_id` for the current tenant and renders
   that template's <Layout> with the matching child page based on the
   sub-path. Falls back to "editorial" while settings are loading or if
   the tenant picked a not-yet-shipped template (e.g. "minimal" before
   we've built it). */

import { useEffect } from "react";
import { Outlet, Route, Routes, useLocation, useParams, useSearchParams } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { resolveTemplate, DEFAULT_TEMPLATE_ID, TEMPLATES } from "@/templates/registry";
import usePageMeta from "@/hooks/usePageMeta";

/* Reset scroll position to top on every route change inside the
   tenant shell. Without this, navigating Portfolio → Services on
   mobile drops the visitor wherever they were scrolled on Portfolio
   (often near the footer) and the new Services page silently starts
   below the masthead — a "page transition feels off" symptom that the
   user spotted across the booking flow specifically. We use
   `pathname` not `key` so a hash-only change (e.g. `#contact` deep
   link) doesn't force a scroll-to-top and the in-page anchor still
   works. The cleanup intentionally does nothing — there's no state to
   tear down. */
function useTenantScrollReset() {
  const { pathname } = useLocation();
  useEffect(() => {
    // `behavior: 'instant'` matches a hard navigation (visitor expects
    // landing at the top, not a slow smooth-scroll dance). Browsers
    // that don't support `instant` fall back to `auto` which is also
    // instant on every modern engine.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch (_) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
}

/* Map the current sub-route to a per-page title + description so each tenant
   page gets unique SEO metadata. Without this, Google sees the studio's
   /portfolio + /services + /contact + /booking pages all as identical "Lumetier
   · The operating system for photographers" — 22 duplicate-title flag from the
   2026-06-27 audit. With it, each page tells crawlers exactly what it is.

   Falls back to the studio name + a generic description while settings load. */
function useTenantPageMeta(settings, slug) {
  const { pathname } = useLocation();
  const studioName = settings?.studio_name || (slug ? slug.replace(/-/g, " ") : "Lumetier studio");
  const heroEyebrow = settings?.hero_eyebrow || "";
  const base = `https://lumetiercreatives.com/s/${slug || ""}`;
  // Match the deepest sub-path under /s/:slug
  const sub = (pathname.split(`/s/${slug}/`)[1] || "").split("/")[0] || "home";

  // Per-tenant Open Graph card — generated dynamically by the backend at
  // /api/s/{slug}/og.png so link previews in iMessage / WhatsApp / Slack /
  // Discord carry the studio's own brand (initials in their accent
  // colour + serif name + tagline) instead of the platform-default
  // "Lumetier" card. We append a cache-buster keyed on the settings'
  // `updated_at` so editing the studio's brand instantly invalidates
  // every CDN that's cached the previous card. Falls back to a
  // timestamp bucket when the field is missing (older docs).
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const cacheBust = (() => {
    const ts = settings?.updated_at || settings?.brand_updated_at;
    if (ts) {
      try { return String(new Date(ts).getTime()); } catch (_) { /* fall through */ }
    }
    // 1-hour bucket — keeps the URL stable across page navigations
    // within the same session but lets new visitors pick up updates
    // within an hour even without an explicit save.
    return String(Math.floor(Date.now() / 3600000));
  })();
  const ogImage = slug ? `${apiBase}/api/s/${slug}/og.png?v=${cacheBust}` : undefined;

  let title, description, canonical;
  switch (sub) {
    case "portfolio":
      title = `Portfolio — ${studioName}`;
      description = `Recent work from ${studioName}. ${heroEyebrow || "Editorial photography sessions."}`;
      canonical = `${base}/portfolio`;
      break;
    case "services":
      title = `Services & pricing — ${studioName}`;
      description = `Photography services and pricing at ${studioName}. Book a session online — deposits, contracts, and gallery delivery all included.`;
      canonical = `${base}/services`;
      break;
    case "booking":
      title = `Book a session — ${studioName}`;
      description = `Reserve your session with ${studioName}. Pick a service, choose a date, pay your deposit — all in one place.`;
      canonical = `${base}/booking`;
      break;
    case "contact":
      title = `Contact — ${studioName}`;
      description = `Get in touch with ${studioName}. Inquiries, partnership requests, custom commissions.`;
      canonical = `${base}/contact`;
      break;
    default:
      title = `${studioName} — ${heroEyebrow || "Photography"}`;
      description = settings?.about_text
        ? settings.about_text.slice(0, 160).trim()
        : `Editorial photography by ${studioName}. View the portfolio, browse services, and book a session.`;
      canonical = base;
  }
  usePageMeta({ title, description, canonical, ogImage });
}

/* Swap the platform favicon for the studio's own — either their
   uploaded `favicon_url` (admin Branding tab) or, when missing, the
   backend's generated SVG (initials on the accent-colour tile at
   /api/s/{slug}/favicon.svg). Stores + restores the previous href so
   navigating BACK to a non-tenant route returns the Lumetier favicon
   without a stale studio mark left in the tab. */
function useTenantFavicon(slug, settings) {
  useEffect(() => {
    if (!slug) return;
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    const href = settings?.favicon_url || `${apiBase}/api/s/${slug}/favicon.svg`;

    // Find an existing favicon link (any size/type) to swap, or create
    // one if the platform <head> didn't ship one (rare).
    let link = document.head.querySelector('link[rel~="icon"]');
    let created = false;
    let prevHref = null;
    let prevType = null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "icon");
      document.head.appendChild(link);
      created = true;
    } else {
      prevHref = link.getAttribute("href");
      prevType = link.getAttribute("type");
    }
    link.setAttribute("href", href);
    link.setAttribute("type", href.endsWith(".svg") ? "image/svg+xml" : "image/png");

    return () => {
      try {
        if (created) {
          link.remove();
        } else {
          if (prevHref != null) link.setAttribute("href", prevHref);
          if (prevType != null) link.setAttribute("type", prevType);
          else link.removeAttribute("type");
        }
      } catch (_) { /* DOM may already be gone on hot reload */ }
    };
  }, [slug, settings?.favicon_url]);
}

/* Inject a high-priority <link rel="preload"> for the tenant's hero image
   into <head> the moment we know the URL. Cuts ~200-400ms off LCP on mobile
   tenant booking/home pages where the hero is the entire above-the-fold.
   We only mount one link at a time (keyed by tenant + URL) and clean it up
   when the URL changes or the shell unmounts so we don't stack duplicates
   across SPA navigations between tenants. */
function useTenantHeroPreload(heroUrl) {
  useEffect(() => {
    if (!heroUrl) return;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = heroUrl;
    link.setAttribute("fetchpriority", "high");
    link.setAttribute("data-tenant-hero-preload", "1");
    document.head.appendChild(link);
    return () => {
      if (link.parentNode) link.parentNode.removeChild(link);
    };
  }, [heroUrl]);
}

export default function TenantTemplateShell() {
  const { slug } = useParams();
  const { settings, loading } = useSiteSettings();
  const [search] = useSearchParams();

  /* ── Preview-before-save override ───────────────────────────────────
     The admin Template Picker passes `?preview_template=<id>` when the
     studio clicks "Preview live →" on a template card. We swap the
     resolved bundle to that template for the visit, but leave the saved
     `template_id` in MongoDB untouched. The studio sees the FULL site
     rendered in the candidate template using their real content/photos
     before committing — exits by closing the tab.
     Only allowed when the id matches a real shipped template; anything
     else is ignored so we can't be coerced into rendering a fake bundle
     via a crafted URL. */
  const rawPreview = search.get("preview_template");
  const previewId = rawPreview && TEMPLATES[rawPreview]?.status === "ready" ? rawPreview : null;
  const templateId = previewId || settings?.template_id || DEFAULT_TEMPLATE_ID;

  const { Layout, Home, Portfolio, Services, Booking, BookingConfirmation, Contact } =
    resolveTemplate(templateId);

  useTenantPageMeta(settings, slug);
  useTenantFavicon(slug, settings);
  useTenantScrollReset();

  // Same fallback chain as the templates use (see editorial/Home.jsx + bold/Home.jsx)
  // so the preload URL matches what the <img src> will resolve to. Skips the
  // remote Unsplash FALLBACK_HERO on purpose — preloading an external CDN we
  // don't own would actually hurt LCP for sites that haven't set a hero yet.
  const heroUrl =
    settings?.hero_image_url ||
    settings?.hero_image ||
    settings?.portfolio_images?.[0] ||
    null;
  useTenantHeroPreload(heroUrl);

  // While site_settings hasn't loaded yet we render the editorial Layout
  // anyway — its content auto-fills from `useSiteSettings()` once data
  // arrives, so the only difference is a brief flash if the tenant has
  // picked a non-default template. Acceptable for v1.
  return (
    <>
      {previewId && <PreviewBanner previewId={previewId} savedId={settings?.template_id} />}
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="services" element={<Services />} />
          <Route path="portfolio" element={<Portfolio />} />
          <Route path="booking" element={<Booking />} />
          <Route path="booking/confirmation/:reference" element={<BookingConfirmation />} />
          <Route path="contact" element={<Contact />} />
        </Route>
      </Routes>
    </>
  );
}

/* Floating "preview mode" badge — only mounts when ?preview_template=<id>
   is in the URL. Pinned to the top of the viewport, semi-transparent so
   it doesn't dominate the page being previewed. The "Exit preview" link
   strips the query param so the studio sees their saved template again
   without a separate page load. */
function PreviewBanner({ previewId, savedId }) {
  const previewLabel = TEMPLATES[previewId]?.label || previewId;
  const savedLabel = TEMPLATES[savedId]?.label || savedId || "Editorial";
  return (
    <div
      data-testid="tenant-preview-banner"
      style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
        background: "rgba(15, 12, 8, 0.92)", color: "#F5F2EC",
        backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        borderBottom: "1px solid rgba(201, 169, 97, 0.4)",
      }}
    >
      <div className="max-w-[1480px] mx-auto px-4 py-2 flex items-center justify-between gap-4 text-[10px] uppercase tracking-[0.32em]">
        <span style={{ color: "#C9A961" }}>● PREVIEWING — {previewLabel}</span>
        <span className="hidden sm:inline opacity-60">Your saved template is {savedLabel}. This view is not live.</span>
        <a href={window.location.pathname} className="underline-offset-4 hover:underline shrink-0" data-testid="tenant-preview-exit">
          Exit preview →
        </a>
      </div>
    </div>
  );
}
